import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";
import { useWebSocket } from "./WebSocketContext";

const ChallengesContext = createContext({
  active: [],
  pendingForMe: [],
  recentWin: null,
  clearRecentWin: () => {},
  refresh: () => {},
});

const POLL_MS = 90_000;
// localStorage key to track which wins we've already shown the celebration
// for, so the user doesn't see "You won!" every time they open the app.
const SEEN_WINS_KEY = "gwg_seen_won_challenges";

function readSeen() {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_WINS_KEY) || "[]")); }
  catch { return new Set(); }
}
function writeSeen(set) {
  try { localStorage.setItem(SEEN_WINS_KEY, JSON.stringify(Array.from(set))); }
  catch { /* ignore */ }
}

export function ChallengesProvider({ children }) {
  const { user } = useAuth();
  const { subscribe } = useWebSocket();
  const [active, setActive] = useState([]);
  // Most recently won challenge that we haven't yet shown the celebration
  // overlay for. Set when refresh() finds a completed challenge with
  // winner_id === user.user_id that's not in localStorage's seen set.
  const [recentWin, setRecentWin] = useState(null);
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) { setActive([]); return; }
    try {
      // Use the broader /challenges list so we can detect wins that have
      // transitioned to "completed" (which fall out of /challenges/active).
      const { data } = await api.get("/challenges");
      const all = Array.isArray(data) ? data : [];
      // Active panel still uses only pending + active.
      setActive(all.filter((c) => c.status === "pending" || c.status === "active"));

      // Check for a fresh win.
      const seen = readSeen();
      const fresh = all.find((c) =>
        c.status === "completed" &&
        c.winner_id === user.user_id &&
        !seen.has(c.challenge_id)
      );
      if (fresh) setRecentWin(fresh);
    } catch { /* offline; keep prior */ }
  }, [user]);

  useEffect(() => {
    refresh();
    if (timerRef.current) clearInterval(timerRef.current);
    if (user) timerRef.current = setInterval(refresh, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user, refresh]);

  useEffect(() => {
    if (!user) return undefined;
    const unsubReq = subscribe("challenge:request", () => refresh());
    const unsubAcc = subscribe("challenge:accepted", () => refresh());
    const unsubDec = subscribe("challenge:declined", () => refresh());
    const unsubScore = subscribe("challenge:score", (payload) => {
      setActive((prev) => prev.map((c) =>
        c.challenge_id === payload.challenge_id
          ? { ...c, challenger_score: payload.challenger_score, opponent_score: payload.opponent_score }
          : c
      ));
    });
    return () => { unsubReq(); unsubAcc(); unsubDec(); unsubScore(); };
  }, [user, subscribe, refresh]);

  const clearRecentWin = useCallback(() => {
    if (recentWin) {
      const seen = readSeen();
      seen.add(recentWin.challenge_id);
      writeSeen(seen);
    }
    setRecentWin(null);
  }, [recentWin]);

  const pendingForMe = active.filter((c) => c.status === "pending" && c.opponent_id === user?.user_id);

  return (
    <ChallengesContext.Provider value={{ active, pendingForMe, recentWin, clearRecentWin, refresh }}>
      {children}
    </ChallengesContext.Provider>
  );
}

export const useChallenges = () => useContext(ChallengesContext);
